import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CInput, CHeader, CLoader } from '../../components/common';
import { FiSearch, FiCheckCircle, FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiVideo, FiMessageSquare } from 'react-icons/fi';
import { IoStar } from 'react-icons/io5';
import { handleExpertSearch, fetchUserBookings, Expert, Booking } from '../../api';

const ExpertSearch: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedTab, setSelectedTab] = useState<'search' | 'bookings' | 'sessions'>('search');
  const [experts, setExperts] = useState<Expert[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (selectedTab === 'search') {
      fetchExperts();
    } else {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, page]);

  const fetchExperts = async (query?: string) => {
    setLoading(true);
    try {
      const response = await handleExpertSearch(query || '', page);
      setExperts(response.experts);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error('Error fetching experts:', error);
      setExperts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const data = await fetchUserBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      fetchExperts(text);
    }, 500);
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString();
  };

  const isUpcoming = (startTime: string) => {
    return new Date(startTime).getTime() + 30 * 60000 > Date.now();
  };

  const ExpertCard = ({ expert }: { expert: Expert }) => (
    <div
      className="rounded-xl p-4 cursor-pointer transition-opacity hover:opacity-90"
      style={{ backgroundColor: colors.inputBackground }}
      onClick={() => navigate(`/expert/${expert.user_id}`)}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
            style={{ backgroundColor: colors.primary, color: colors.white }}
          >
            {expert.name.charAt(0)}
          </div>
          {expert.user_avg_rating > 0 ? (
            <div className="flex items-center gap-1 mt-1">
              {React.createElement(IoStar as any, { size: 12, color: '#FFD700' })}
              <CText type="r12" color={colors.grayScale500}>
                {expert.user_avg_rating.toFixed(1)}
              </CText>
            </div>
          ) : (
            <CText type="r10" color={colors.grayScale500} className="mt-1">
              No Rating
            </CText>
          )}
        </div>

        {/* Details */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <CText type="B16" color={colors.textColor}>
              {expert.name}
            </CText>
            {expert.is_verified &&
              React.createElement(FiCheckCircle as any, { size: 16, color: colors.primary })
            }
          </div>
          {expert.company_associated && (
            <CText type="M12" color={colors.white}>
              Associated with {expert.company_name}
            </CText>
          )}
          <CText type="r14" color={colors.grayScale500}>
            {expert.sebi_qualification}
          </CText>
          <CText type="r12" color={colors.grayScale400}>
            SEBI Reg No. - <span style={{ color: colors.primary }}>{expert.sebi_reg_number}</span>
          </CText>
          <CText type="r12" color={colors.grayScale400}>
            Languages: {expert.languages.join(', ')}
          </CText>
          <CText type="r12" color={colors.grayScale400}>
            Exp: {expert.years_of_xp} yr(s)
          </CText>
        </div>
      </div>

      {/* Rates */}
      <div className="flex justify-between mt-3 pt-3 border-t" style={{ borderColor: colors.borderColor }}>
        <CText type="r14" color={colors.textColor}>
          ₹{expert.call_session_rate}/30 mins Call
        </CText>
        <CText type="r14" color={colors.textColor}>
          ₹{expert.chat_session_rate}/30 mins Chat
        </CText>
      </div>
    </div>
  );

  const BookingCard = ({ booking, isPast }: { booking: Booking; isPast: boolean }) => (
    <div
      className="rounded-xl p-4"
      style={{ backgroundColor: colors.inputBackground }}
    >
      <div className="flex items-center gap-2 mb-2">
        {React.createElement(
          booking.booking_type === 'V' ? FiVideo : FiMessageSquare as any,
          { size: 16, color: colors.white }
        )}
        <CText type="B14" color={colors.textColor}>
          {booking.expert}
        </CText>
      </div>
      <div className="flex items-center gap-2 mb-1">
        {React.createElement(FiCalendar as any, { size: 14, color: colors.grayScale500 })}
        <CText type="r12" color={colors.textColor}>
          {formatDate(booking.start_time)}
        </CText>
      </div>
      <div className="flex items-center gap-2 mb-2">
        {React.createElement(FiClock as any, { size: 14, color: colors.grayScale500 })}
        <CText type="r12" color={colors.textColor}>
          {formatTime(booking.start_time)}
        </CText>
      </div>

      {!isPast ? (
        <>
          <div
            className="text-sm cursor-pointer mb-3"
            style={{ color: colors.primary, textDecoration: 'underline' }}
          >
            {booking.booking_type === 'V' ? 'Join Call' : 'Chat'}
          </div>
          <div className="flex gap-2">
            <button
              className="flex-1 py-2 rounded-lg border text-sm"
              style={{ borderColor: colors.borderColor, color: colors.primary }}
            >
              Re-Schedule
            </button>
            <button
              className="flex-1 py-2 rounded-lg border text-sm"
              style={{ borderColor: colors.borderColor, color: colors.alertColor }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        !booking.is_rated && (
          <div
            className="text-sm cursor-pointer"
            style={{ color: colors.primary, textDecoration: 'underline' }}
          >
            Rate Expert
          </div>
        )
      )}
    </div>
  );

  const tabs = [
    { key: 'bookings', label: 'Bookings' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'search', label: 'Expert Search' },
  ];

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-5xl lg:mx-auto">
      {loading && <CLoader />}

      <CHeader title="Expert Opinion" />

      {/* Tab Selector */}
      <div
        className="flex rounded-full p-1 mb-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className="flex-1 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              backgroundColor: selectedTab === tab.key ? colors.primary : 'transparent',
              color: selectedTab === tab.key ? colors.white : colors.grayScale400,
            }}
            onClick={() => setSelectedTab(tab.key as typeof selectedTab)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Bookings Tab */}
      {selectedTab === 'bookings' && (
        <div>
          {bookings.filter((b) => isUpcoming(b.start_time)).length === 0 ? (
            <CText type="r14" color={colors.grayScale500} className="text-center py-8">
              No upcoming bookings
            </CText>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookings
                .filter((b) => isUpcoming(b.start_time))
                .map((booking) => (
                  <BookingCard key={booking.id} booking={booking} isPast={false} />
                ))}
            </div>
          )}
        </div>
      )}

      {/* Sessions Tab (Past) */}
      {selectedTab === 'sessions' && (
        <div>
          {bookings.filter((b) => !isUpcoming(b.start_time)).length === 0 ? (
            <CText type="r14" color={colors.grayScale500} className="text-center py-8">
              No past sessions
            </CText>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookings
                .filter((b) => !isUpcoming(b.start_time))
                .map((booking) => (
                  <BookingCard key={booking.id} booking={booking} isPast={true} />
                ))}
            </div>
          )}
        </div>
      )}

      {/* Expert Search Tab */}
      {selectedTab === 'search' && (
        <>
          {/* Search */}
          <div className="mb-4">
            <CInput
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search experts..."
              insideLeftIcon={() =>
                React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })
              }
            />
          </div>

          {/* Pagination */}
          <div className="flex justify-end items-center gap-4 mb-4">
            <button
              className="p-1"
              style={{ opacity: page === 1 ? 0.5 : 1 }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {React.createElement(FiChevronLeft as any, { size: 16, color: colors.textColor })}
            </button>
            <CText type="r12" color={colors.textColor}>
              {page} / {totalPages}
            </CText>
            <button
              className="p-1"
              style={{ opacity: page === totalPages ? 0.5 : 1 }}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              {React.createElement(FiChevronRight as any, { size: 16, color: colors.textColor })}
            </button>
          </div>

          {/* Expert List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {experts.map((expert) => (
              <ExpertCard key={expert.user_id} expert={expert} />
            ))}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default ExpertSearch;
